import {
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  DragTypeStationEnum,
  Log,
  NotDraggingDataConst,
  SimpleResult,
  stationRouteDraggingDataAtom,
  stationRouteDraggingStationsTempIdsAtom,
  TDragDropStationRouteSortable,
  TDragStationRouteData,
  warnAndToastSimpleResultWithFallback,
} from "@ryde/shared";

import { useDragStationRouteStationsReorder } from "./use-drag-station-route-reorder";

interface IUseStationRoutesDragHandling {
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: (event: DragCancelEvent) => void;
}

export const useStationRoutesDragHandling =
  (): IUseStationRoutesDragHandling => {
    const { t } = useTranslation();
    const setStationRouteDraggingData = useSetRecoilState(
      stationRouteDraggingDataAtom,
    );
    const {
      dragReorderDistancesStationRoute: dragReorderStationsStationRoute,
    } = useDragStationRouteStationsReorder();

    const setTempDragStationsIds = useSetRecoilState(
      stationRouteDraggingStationsTempIdsAtom,
    );

    const dragCleanup = useEvent(() => {
      setTempDragStationsIds(undefined);
    });

    const processDropAction = useEvent(
      async (
        dragSource: TDragDropStationRouteSortable,
        dropTarget: TDragDropStationRouteSortable,
      ): Promise<SimpleResult<void>> => {
        Log.debug(
          "Start processing drop operation from source:",
          dragSource,
          "drop target:",
          dropTarget,
        );
        const dragType = dragSource.type;

        if (dragType === DragTypeStationEnum.STATION) {
          const source = dragSource;
          const destination = dropTarget;
          return dragReorderStationsStationRoute(source, destination);
        }

        return new SimpleResult({
          success: false,
          error: t("errors.invalidDragType"),
        });
      },
    );

    const handleDragStart = useEvent((event: DragStartEvent): void => {
      const { active } = event;
      const { data } = active;
      const dragData =
        (data.current as TDragStationRouteData) || NotDraggingDataConst;
      const dragTypeId = dragData.type;

      Log.debug(`Start dragging of type:${dragTypeId}`, data.current, dragData);

      setStationRouteDraggingData(dragData);
    });

    const handleDragEnd = useEvent(
      async (event: DragEndEvent): Promise<void> => {
        const { active, over } = event;
        const { data: dataSource } = active;
        const { data: dataTarget } = over || {};
        Log.debug("Handle drag end, source data:", { ...dataSource.current });
        Log.debug("Handle drag end, target data:", { ...dataTarget?.current });

        if (!dataSource.current) {
          Log.warnAndToast(
            t("errors.invalidDragSourceData"),
            dataSource.current,
          );
          dragCleanup();
          return;
        }

        if (!dataTarget?.current) {
          Log.warnAndToast(
            t("errors.invalidDragSourceData"),
            dataTarget?.current,
          );
          dragCleanup();
          return;
        }

        const dragTypeId = dataSource.current.type;
        const sourceData = {
          ...dataSource.current,
        } as TDragDropStationRouteSortable;
        const targetData = {
          ...dataTarget.current,
        } as TDragDropStationRouteSortable;

        try {
          const result = await processDropAction(sourceData, targetData);
          if (!result.success) {
            warnAndToastSimpleResultWithFallback(
              result,
              t,
              "errors.errorInDragOperation",
            );
          }
          setStationRouteDraggingData(NotDraggingDataConst);
          Log.debug(
            `Finish drag processing or type ${dragTypeId}, source data:`,
            sourceData,
            " target data:",
            targetData,
          );
          return;
        } catch (e) {
          Log.warnAndToast(t("errors.errorInDragOperation"), e);
          return;
        } finally {
          dragCleanup();
        }
      },
    );

    const handleDragOver = useEvent((event: DragOverEvent): void => {
      const { active, over } = event;
      const { data } = active;

      const dragTypeId = data?.current?.type;
      Log.debug(
        `Drag over:${dragTypeId}, payload:`,
        data?.current?.payload,
        "over item data:",
        over?.data?.current,
      );
    });

    const handleDragCancel = useEvent((event: DragCancelEvent): void => {
      dragCleanup();
      Log.debug(`Drag canceled`, event?.active?.data?.current?.payload); // TODO: add translation solution/key ? add as toast?
    });

    return { handleDragStart, handleDragOver, handleDragEnd, handleDragCancel };
  };
